import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../../../Common/Menus/FixedMenu.jsx';

const LayerMoveMenu = ({
  expanded,
  setExpanded,
  targetLabel,
  parentCandidates,
  onMove,
}) => {
  const [parentId, setParentId] = useState('');
  const [slotId, setSlotId] = useState('');
  const [error, setError] = useState('');
  const selectedParent = useMemo(
    () => parentCandidates.find((candidate) => candidate.id === parentId) || null,
    [parentCandidates, parentId]
  );
  const selectedSlot = selectedParent?.slots.find((slot) => slot.id === slotId) || null;

  useEffect(() => {
    if (!expanded) {
      setParentId('');
      setSlotId('');
      setError('');
    }
  }, [expanded]);

  useEffect(() => {
    if (parentId && !selectedParent) {
      setParentId('');
      setSlotId('');
    }
  }, [parentId, selectedParent]);

  const confirmMove = () => {
    if (!selectedParent || !selectedSlot) {
      setError('Choose where you want to place this item.');
      return;
    }

    if (!onMove(selectedParent.id, selectedSlot.id)) {
      setError('That destination is no longer available. Choose another position.');
    }
  };

  return (
    <FixedMenu
      className="new-blog-fixed-menu"
      animate
      title="Move Into Another Section"
      iconClass="bi-box-arrow-in-down"
      expanded={expanded}
      setExpanded={setExpanded}
    >
      <p className="small text-muted mb-3">
        Place <strong>{targetLabel}</strong> inside another nearby section.
      </p>

      {error ? <div className="alert alert-danger small" role="alert">{error}</div> : null}

      <section className="mb-4">
        <h6 className="layer-move-heading">1. Choose a section</h6>
        {parentCandidates.length ? (
          <div className="layer-parent-grid">
            {parentCandidates.map((candidate) => (
              <button
                className={`btn btn-sm text-start ${candidate.id === parentId ? 'btn-primary' : 'btn-outline-secondary'}`}
                type="button"
                aria-pressed={candidate.id === parentId}
                onClick={() => {
                  setParentId(candidate.id);
                  setSlotId('');
                  setError('');
                }}
                title={`Move into ${candidate.label}`}
                key={candidate.id}
              >
                <i className="bi bi-box me-2" aria-hidden="true"></i>
                <span className="text-truncate">{candidate.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="small text-muted mb-0">There are no nearby sections that can hold this item.</p>
        )}
      </section>

      {selectedParent ? (
        <section className="mb-4">
          <h6 className="layer-move-heading">2. Choose a position</h6>
          <div className="layer-position-list" role="radiogroup" aria-label="Position inside section">
            {selectedParent.slots.map((slot) => (
              <button
                className={`btn btn-sm text-start ${slot.id === slotId ? 'btn-primary' : 'btn-outline-secondary'}`}
                type="button"
                role="radio"
                aria-checked={slot.id === slotId}
                onClick={() => {
                  setSlotId(slot.id);
                  setError('');
                }}
                key={slot.id}
              >
                <i className="bi bi-arrow-return-right me-2" aria-hidden="true"></i>
                <span>{slot.label}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <div className="layer-move-summary mb-3" aria-live="polite">
        {selectedParent && selectedSlot
          ? <><strong>Destination:</strong> {selectedParent.label} - {selectedSlot.label}</>
          : 'Choose a section and position.'}
      </div>

      <button
        className="btn btn-primary w-100"
        type="button"
        onClick={confirmMove}
        disabled={!selectedParent || !selectedSlot}
      >
        <i className="bi bi-check-lg me-2" aria-hidden="true"></i>
        Move element
      </button>
    </FixedMenu>
  );
};

const slotShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  targetId: PropTypes.string.isRequired,
  position: PropTypes.oneOf(['before', 'after', 'child']).isRequired,
});

LayerMoveMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  targetLabel: PropTypes.string.isRequired,
  parentCandidates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    slots: PropTypes.arrayOf(slotShape).isRequired,
  })),
  onMove: PropTypes.func.isRequired,
};

LayerMoveMenu.defaultProps = {
  parentCandidates: [],
};

export default LayerMoveMenu;
